import React, { useState, useEffect, useMemo } from "react";
import {
  Pencil,
  X,
  Check,
  ChevronRight,
  Camera,
  CalendarDays,
  MapPin,
  IndianRupee,
  Ruler,
  Clock3,
  ClipboardList,
  Building2,
  Layers3,
  FileText,
  Hash,
  Landmark,
  UserRound,
  RefreshCcw,
  AlertCircle,
  ImagePlus,
} from "lucide-react";
import { MdDelete } from "react-icons/md";
import axios from "axios";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";
import { useNavigate } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

const formatDisplayDate = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return "Not set";
  return "Rs. " + amount.toLocaleString("en-IN");
};

const formatAreaUnit = (unit) => {
  const unitMap = {
    m_sq: "m sq",
    ft_sq: "ft sq",
    acre: "acre",
    hectare: "hectare",
  };
  return unitMap[unit] || unit || "";
};

const getStoredOrganizationName = () => {
  try {
    const organization = JSON.parse(localStorage.getItem("organization") || "{}");
    return organization?.organization_name || "";
  } catch (error) {
    return "";
  }
};

const getStatusBadgeClass = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "completed":
      return "bg-emerald-50 text-emerald-700";
    case "halted":
      return "bg-rose-50 text-rose-700";
    case "upcoming":
      return "bg-amber-50 text-amber-700";
    default:
      return "bg-teal-50 text-teal-700";
  }
};

const getDaysLeft = (handoverDate) => {
  if (!handoverDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(handoverDate);
  if (Number.isNaN(target.getTime())) return null;
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
};

const detailFieldLabelClass = "text-[13px] font-semibold text-[#374151]";
const detailFieldInputClass =
  "mt-1 w-full rounded-xl border border-[#BFC9D9] bg-white px-3 py-2.5 text-sm font-medium text-[#111827] shadow-[inset_0_1px_2px_rgba(15,23,42,0.04)] focus:outline-none focus:ring-2 focus:ring-[#C084FC]";

const MetricCard = ({ icon: Icon, label, value, valueClassName = "" }) => (
  <div className="rounded-lg border border-[#D8E0EC] bg-white p-3 shadow-[0_8px_24px_rgba(15,23,42,0.05)]">
    <div className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#4B5563]">
      <Icon className="h-4 w-4 text-[#374151]" />
      <span>{label}</span>
    </div>
    <div
      className={`text-lg font-bold text-[#0F172A] ${valueClassName}`}
    >
      {value}
    </div>
  </div>
);

const DetailRow = ({ icon: Icon, label, value, accent }) => (
  <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] items-start gap-3 border-t border-[#E4E9F1] py-3 first:border-t-0 first:pt-0 last:pb-0">
    <div className="flex items-center gap-2 text-[#374151]">
      <Icon className={`h-4 w-4 ${accent || ""}`} />
      <span className="text-sm font-semibold">{label}</span>
    </div>
    <div className="text-sm font-semibold text-[#111827]">{value}</div>
  </div>
);

const SiteDetails = ({ userPermissions }) => {
  const { addAlert } = useAlert();
  // Normalize and map state names from the PIN API to your <states> array
  const norm = (s = "") =>
    s.toLowerCase().replace(/&/g, "and").replace(/\s+/g, " ").trim();

  // Common aliases seen in PIN APIs
  const STATE_ALIAS_MAP = {
    "nct of delhi": "Delhi",
    "delhi (nct)": "Delhi",
    "jammu & kashmir": "Jammu and Kashmir",
    "andaman & nicobar islands": "Andaman and Nicobar Islands",
    "dadra & nagar haveli and daman & diu":
      "Dadra and Nagar Haveli and Daman and Diu",
    pondicherry: "Puducherry",
    uttaranchal: "Uttarakhand",
  };

  const mapToOfficialState = (rawState, statesList) => {
    if (!rawState) return "";
    const n = norm(rawState);
    const alias = STATE_ALIAS_MAP[n];
    const candidate = alias || rawState;

    // Exact (normalized) match against the options you already fetched
    const exact = statesList.find((s) => norm(s) === norm(candidate));
    if (exact) return exact;

    // Fallback: loose contains check
    const loose = statesList.find(
      (s) => norm(s).includes(n) || n.includes(norm(s)),
    );
    return loose || candidate; // fallback to raw if no match found
  };
  const navigate = useNavigate();
  const [siteData, setSiteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [adminBodies, setAdminBodies] = useState([]);
  const [editPermsission, setEditPermission] = useState(false);
  const [editMode, setEditMode] = useState({
    site_name: false,
    site_area: false,
    local_admin_body_name: false,
    address: false,
    site_catagory_id: false,
    start_date: false,
    constructionStatus: false,
    description: false,
  });

  const [editModalOpen, setEditModalOpen] = useState(false);

  // Delete confirmation modal state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    site_name: "",
    site_area: "",
    site_unit: "m_sq",
    local_admin_body_name: "",
    local_admin_body_id: "",
    address: "",
    site_catagory_id: "",
    start_date: "",
    handover_date: "",
    constructionStatus: "",
    construction_stage: "",
    description: "",
    esrimated_cost: "",
    state: "",
    city: "",
    pincode: "",
    latitude: "",
    longitude: "",
  });
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await axios.post(
          "https://countriesnow.space/api/v0.1/countries/states",
          { country: "India" },
        );
        setStates(res.data.data.states.map((s) => s.name));
      } catch (err) {
        console.error("Failed to fetch states:", err);
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    const fetchCities = async () => {
      if (!formData.state) return;
      try {
        const res = await axios.post(
          "https://countriesnow.space/api/v0.1/countries/state/cities",
          {
            country: "India",
            state: formData.state,
          },
        );
        console.log("Cities", res.data);
        setCities(res.data.data);
      } catch (err) {
        console.error("Failed to fetch cities:", err);
      }
    };
    fetchCities();
  }, [formData.state]);

  const constructionStatusOptions = [
    "Completed",
    "Upcoming",
    "Halted",
    "Ongoing",
  ];
  const constructionStageOptions = [
    "Site Preparation",
    "Foundation",
    "Plinth",
    "Structure",
    "Masonry",
    "MEP",
    "Finishing",
    "Handover",
  ];

  const LoadingSkeleton = () => (
    <div className="w-full mx-auto p-4">
      {/* Hero Image Skeleton */}
      <Skeleton className="h-48 w-full rounded-lg mb-4" />

      {/* Header Skeleton */}
      <div className="flex justify-between items-center mb-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-6 w-20" />
      </div>

      {/* Content Skeleton */}
      <div className="bg-white rounded-lg">
        {[1, 2, 3, 4, 5, 6, 7].map((index) => (
          <div key={index} className="border-b border-gray-200 py-4">
            <div className="flex justify-between items-center mb-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-4 rounded-full" />
            </div>
            <Skeleton className="h-6 w-3/4" />
          </div>
        ))}
      </div>
    </div>
  );

  const fetchAdminBodies = async () => {
    try {
      const token = localStorage.getItem("access");
      const response = await axios.get(
        `${BASE_URL}/sites/get-all-localadminbody/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setAdminBodies(response.data.data);
    } catch (error) {
      console.error("Error fetching admin bodies:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("access");
      const response = await axios.get(
        `${BASE_URL}/sites/get-all-sitecatagory/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setCategories(response.data.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchSiteDetails = async () => {
    try {
      const siteId = localStorage.getItem("site");
      const token = localStorage.getItem("access");
      const response = await axios.get(
        `${BASE_URL}/sites/get-a-sites/${siteId}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setSiteData(response.data.data);
      if (response?.data?.data) {
        localStorage.setItem("siteData", JSON.stringify(response.data.data));
      }

      const matchedAdminBody = adminBodies.find(
        (ab) => ab.id === response.data.data.local_admin_body_id,
      );

      setFormData({
        site_name: response.data.data.site_name,
        site_area: response.data.data.site_area,
        site_unit: response.data.data.uom,
        local_admin_body_name:
          matchedAdminBody?.admin_body_type ||
          response.data.data.local_admin_body_name,

        local_admin_body_id: response.data.data.local_admin_body_id,
        address: response.data.data.address,
        site_catagory_id: response.data.data.site_catagory_id,
        start_date: response.data.data.start_date,
        handover_date: response.data.data.handover_date || "",
        constructionStatus: response.data.data.constructionStatus || "Ongoing",
        construction_stage:
          response.data.data.construction_stage ||
          response.data.data.constructionStage ||
          response.data.data.project_stage ||
          "",
        description: response.data.data.description || "",
        esrimated_cost: response.data.data.esrimated_cost || "",
        city: response.data.data.city || "",
        state: response.data.data.state || "",
        pincode: response.data.data.pincode || "",
        latitude:
          response.data.data.latitude === null ||
          response.data.data.latitude === undefined
            ? ""
            : String(response.data.data.latitude),
        longitude:
          response.data.data.longitude === null ||
          response.data.data.longitude === undefined
            ? ""
            : String(response.data.data.longitude),
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching site details:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSiteDetails();
    fetchCategories();
    fetchAdminBodies();
  }, []);
  useEffect(() => {
    if (adminBodies.length > 0) {
      fetchSiteDetails();
    }
  }, [adminBodies]);

  const handleUpdate = async (field, value = null) => {
    try {
      const siteId = localStorage.getItem("site");
      const token = localStorage.getItem("access");
      let updateData = {};

      switch (field) {
        case "site_name":
          updateData = { site_name: formData.site_name };
          break;
        case "site_area":
          updateData = {
            site_area: parseFloat(formData.site_area),
            uom: formData.site_unit,
          };
          break;
        case "local_admin_body_name":
          updateData = {
            local_admin_body_name: formData.local_admin_body_name,
            local_admin_body_id: formData.local_admin_body_id,
          };
          break;
        case "site_catagory_id":
          updateData = { site_catagory_id: formData.site_catagory_id };
          break;
        case "constructionStatus":
          updateData = { constructionStatus: formData.constructionStatus };
          break;
        case "description":
          updateData = { description: formData.description };
          break;
        case "image_file":
          // Special handling for image file
          // The second argument to handleUpdate should be the file object
          // We need to adjust the function signature or arguments usage
          // But here I'm assuming I passed the file as the second argument to handleUpdate
          // Let's fix the function signature in the next step or assume arguments[1]
          break;

        default:
          updateData = { [field]: formData[field] };
      }

      let payload = updateData;
      let headers = {
        Authorization: `Bearer ${token}`,
      };

      if (field === "image_file") {
        const file = value;
        const formDataObj = new FormData();
        formDataObj.append("image_file", file);
        payload = formDataObj;
        headers["Content-Type"] = "multipart/form-data";
      }

      await axios.patch(`${BASE_URL}/sites/update-sites/${siteId}/`, payload, {
        headers: headers,
      });

      setEditMode({ ...editMode, [field]: false });
      await fetchSiteDetails();
    } catch (error) {
      console.error("Error updating site details:", error);
    }
  };

  const handlePinChange = async (pin) => {
    if (!/^\d{6}$/.test(pin)) return; // guard
    const options = {
      method: "GET",
      url: "https://indian-pincode-2024.p.rapidapi.com/pincode",
      params: { pincode: pin },
      headers: {
        "x-rapidapi-key": "f01ac027f0msh4661f5f3d5db7e1p1da2e3jsn8ce0dcc2d48e",
        "x-rapidapi-host": "indian-pincode-2024.p.rapidapi.com",
      },
    };
    try {
      const res = await axios.request(options);
      const api = res?.data?.data || {};

      // Prefer district_name for "city", then division_name, then office_name
      const rawCity =
        api.district_name ||
        api.division_name ||
        api.office_name ||
        api.block_name ||
        "";

      // Map the API's state into your <states> list so the select shows the correct value
      const officialState = mapToOfficialState(
        api.state_name || api.state,
        states,
      );

      setFormData((prev) => ({
        ...prev,
        state: officialState,
        city: rawCity,
      }));
    } catch (error) {
      console.error("Error fetching pincode details:", error);
    }
  };

  // Delete site handler
  const handleDeleteSite = async () => {
    try {
      setDeleting(true);
      const siteId = siteData?.id || localStorage.getItem("site");
      const token = localStorage.getItem("access");
      if (!siteId) {
        addAlert("Unable to determine site id", "error");
        setDeleting(false);
        return;
      }

      await axios.delete(`${BASE_URL}/sites/delete-sites/${siteId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // cleanup and navigate
      localStorage.removeItem("site");
      localStorage.removeItem("siteData");
      addAlert("Site deleted successfully", "success");
      // small delay to allow user to see alert
      setTimeout(() => {
        navigate("/dashboard/projects");
      }, 150);
    } catch (err) {
      console.error("Error deleting site:", err);
      addAlert("Failed to delete site", "error");
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (userPermissions.is_admin) {
      setEditPermission(true);
    }
  }, [userPermissions]);

  const totalFields = Object.keys(formData).length;
  const incompleteFields = Object.values(formData).filter(
    (val) => val === "" || val === null || val === undefined,
  ).length;
  const completionPercent = Math.round(
    ((totalFields - incompleteFields) / totalFields) * 100,
  );
  const categoryName =
    categories.find((cat) => cat.id === siteData?.site_catagory_id)
      ?.catagory_name || "Not set";
  const displayArea =
    siteData?.site_area && siteData?.uom
      ? `${siteData.site_area} ${formatAreaUnit(siteData.uom)}`
      : siteData?.site_area
        ? `${siteData.site_area}`
        : "Not set";
  const displayStatus = formData.constructionStatus || "Ongoing";
  const stageValue =
    siteData?.construction_stage ||
    siteData?.constructionStage ||
    siteData?.project_stage ||
    "";
  const gpsCoordinates =
    siteData?.latitude != null && siteData?.longitude != null
      ? `${siteData.latitude}, ${siteData.longitude}`
      : null;
  const organizationName =
    siteData?.organization_name ||
    siteData?.organisation_name ||
    getStoredOrganizationName() ||
    "Organization";
  const daysLeft = getDaysLeft(siteData?.handover_date);
  const missingItems = useMemo(() => {
    const items = [];
    if (!stageValue) items.push("Construction stage");
    if (!siteData?.description?.trim()) items.push("Description");
    if (!siteData?.latitude && !siteData?.longitude)
      items.push("GPS coordinates");
    if (!siteData?.address?.trim()) items.push("Address");
    return items;
  }, [siteData, stageValue]);

  return loading ? (
    <LoadingSkeleton />
  ) : (
    <div
      className="w-full mx-auto bg-[#FCFCFE] px-4 pb-10 pt-4 lg:px-6"
      style={{
        height: "90vh",
        overflowY: "scroll",
        scrollbarWidth: "none",
      }}
    >
      <div className="mx-auto max-w-[1600px] space-y-5">
        <div className="flex items-center gap-2 text-sm text-[#6B7280]">
          <button
            onClick={() => window.history.back()}
            className="rounded-full p-2 transition hover:bg-white"
          >
            <IoArrowBack size={18} />
          </button>
          <span className="font-medium">Projects</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="font-semibold text-[#111827]">Site Details</span>
        </div>

        <section className="overflow-hidden rounded-lg border border-[#EEF0F6] bg-white p-3.5 shadow-[0_14px_34px_rgba(15,23,42,0.045)] lg:p-4">
          <div className="flex flex-col gap-3.5 xl:flex-row xl:items-start xl:justify-between">
            <div className="flex flex-col gap-3 md:flex-row md:items-start">
              <div className="relative h-36 w-36 overflow-hidden rounded-lg border border-[#EEF0F6] bg-[#F5F7FB]">
                {siteData?.image_file ? (
                  <img
                    src={siteData.image_file}
                    alt={siteData?.site_name || "Site"}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-[#C0C6D4]">
                    <Camera className="h-10 w-10" />
                    <span className="text-base font-medium">No photo</span>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  id="site-image-upload"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleUpdate("image_file", file);
                  }}
                />
                {editPermsission && (
                  <label
                    htmlFor="site-image-upload"
                    className="absolute bottom-3 right-3 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-white text-[#3B0069] shadow-[0_10px_24px_rgba(59,0,105,0.18)] transition hover:scale-105"
                  >
                    <ImagePlus className="h-5 w-5" />
                  </label>
                )}
              </div>

              <div className="flex-1">
                <div className="mb-1 flex flex-wrap items-start gap-2">
                  {!editMode.site_name ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <h1 className="text-2xl font-semibold tracking-[-0.03em] text-[#111827]">
                        {siteData?.site_name || "Untitled Project"}
                      </h1>
                      {editPermsission && (
                        <button
                          onClick={() =>
                            setEditMode({ ...editMode, site_name: true })
                          }
                          className="rounded-full border border-[#E7EAF2] p-2 text-[#6B7280] transition hover:bg-[#F9FAFB]"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="text"
                        value={formData.site_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            site_name: e.target.value,
                          })
                        }
                        className="rounded-2xl border border-[#D9DFEA] px-4 py-2 text-lg font-semibold text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#E9D9FF]"
                      />
                      <button
                        onClick={() =>
                          setEditMode({ ...editMode, site_name: false })
                        }
                        className="rounded-full border border-[#E7EAF2] p-2 text-[#6B7280]"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleUpdate("site_name")}
                        className="rounded-full bg-[#3B0069] p-2 text-white"
                      >
                        <Check className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                <p className="mb-3 text-sm text-[#9CA3AF]">
                  {organizationName}
                </p>

                <div
                  className="mb-3 flex flex-nowrap items-center gap-2 overflow-x-auto pb-1"
                  style={{ scrollbarWidth: "none" }}
                >
                  {editMode.constructionStatus ? (
                    <select
                      value={formData.constructionStatus}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          constructionStatus: e.target.value,
                        }))
                      }
                      onBlur={() => {
                        handleUpdate("constructionStatus");
                        setEditMode((prev) => ({
                          ...prev,
                          constructionStatus: false,
                        }));
                      }}
                      className="rounded-full border border-[#D9DFEA] px-4 py-2 text-[13px] font-semibold text-[#111827] focus:outline-none"
                    >
                      {constructionStatusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <button
                      type="button"
                      disabled={!editPermsission}
                      onClick={() =>
                        editPermsission &&
                        setEditMode((prev) => ({
                          ...prev,
                          constructionStatus: true,
                        }))
                      }
                      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-2 text-[12px] font-semibold ${getStatusBadgeClass(displayStatus)}`}
                    >
                      <span className="h-2 w-2 rounded-full bg-current opacity-80" />
                      {displayStatus}
                    </button>
                  )}

                  <div className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#FFF6E5] px-3 py-2 text-[12px] font-semibold text-[#B26717]">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {stageValue ? stageValue : "Stage not set"}
                  </div>
                  <div className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#F3EFFF] px-3 py-2 text-[12px] font-semibold text-[#7C3AED]">
                    <Ruler className="h-3.5 w-3.5" />
                    {displayArea}
                  </div>
                  {daysLeft !== null && (
                    <div className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#F1EDFF] px-3 py-2 text-[12px] font-semibold text-[#8B5CF6]">
                      <Clock3 className="h-3.5 w-3.5" />
                      {daysLeft >= 0
                        ? `${daysLeft} days left`
                        : `${Math.abs(daysLeft)} days overdue`}
                    </div>
                  )}
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between text-base text-[#B1B7C5]">
                    <span>Profile completion</span>
                    <span className="font-semibold text-[#111827]">
                      {completionPercent}%
                    </span>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-[#F3F4F8]">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#C8B4FF] via-[#9B7DFF] to-[#6D3CF5]"
                      style={{ width: `${completionPercent}%` }}
                    />
                  </div>
                  {missingItems.length > 0 && (
                    <div className="mt-2 flex items-center gap-2 text-[13px] font-medium text-[#C0812F]">
                      <AlertCircle className="h-3.5 w-3.5" />
                      <span>Fill in: {missingItems.join(" . ")}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="ml-auto flex shrink-0 flex-row items-center gap-2 self-start">
              <button
                onClick={() => setEditModalOpen(true)}
                className="rounded-lg border border-[#D7DBE6] bg-white px-4 py-1.5 text-[13px] font-semibold text-[#111827] transition hover:bg-[#F9FAFB] min-w-[92px]"
              >
                Edit
              </button>
              {editPermsission && (
                <button
                  onClick={() => setDeleteConfirmOpen(true)}
                  className="rounded-lg border border-[#FFD7DA] bg-[#FFF4F5] px-3.5 py-1.5 text-[12px] font-semibold text-[#C62839] transition hover:bg-[#FFECEE] whitespace-nowrap"
                >
                  Delete Project
                </button>
              )}
            </div>
          </div>
        </section>

        <section className="grid gap-4 xl:grid-cols-4">
          <MetricCard
            icon={CalendarDays}
            label="Start Date"
            value={formatDisplayDate(siteData?.start_date)}
          />
          <MetricCard
            icon={CalendarDays}
            label="Handover"
            value={formatDisplayDate(siteData?.handover_date)}
          />
          <MetricCard icon={Ruler} label="Site Area" value={displayArea} />
          <MetricCard
            icon={IndianRupee}
            label="Est. Cost"
            value={formatCurrency(siteData?.esrimated_cost)}
          />
        </section>

        <section className="grid gap-4 xl:grid-cols-2">
          <div className="rounded-lg border border-[#EEF0F6] bg-white p-4 shadow-[0_14px_34px_rgba(15,23,42,0.04)]">
            <div className="mb-4 flex items-center gap-2 text-[15px] font-semibold text-[#1F2937]">
              <ClipboardList className="h-5 w-5 text-[#8B5CF6]" />
              <span>Project details</span>
            </div>
            <div>
              <DetailRow
                icon={FileText}
                label="Site name"
                value={siteData?.site_name || "Not set"}
              />
              <DetailRow
                icon={Clock3}
                label="Status"
                value={
                  <span
                    className={`inline-flex rounded-full px-3.5 py-1.5 text-[13px] font-semibold ${getStatusBadgeClass(displayStatus)}`}
                  >
                    {displayStatus}
                  </span>
                }
              />
              <DetailRow
                icon={Building2}
                label="Construction stage"
                value={
                  stageValue ? (
                    stageValue
                  ) : (
                    <span className="italic font-medium text-[#C0C6D4]">
                      Not set
                    </span>
                  )
                }
              />
              <DetailRow
                icon={Layers3}
                label="Site category"
                value={categoryName}
              />
              <DetailRow
                icon={FileText}
                label="Description"
                value={
                  siteData?.description?.trim() ? (
                    siteData.description
                  ) : (
                    <span className="italic font-medium text-[#C0C6D4]">
                      No description added
                    </span>
                  )
                }
              />
            </div>
          </div>

          <div className="rounded-lg border border-[#EEF0F6] bg-white p-4 shadow-[0_14px_34px_rgba(15,23,42,0.04)]">
            <div className="mb-4 flex items-center gap-2 text-[15px] font-semibold text-[#1F2937]">
              <MapPin className="h-5 w-5 text-[#8B5CF6]" />
              <span>Location</span>
            </div>
            <div>
              <DetailRow
                icon={MapPin}
                label="Address"
                value={siteData?.address || "Not set"}
              />
              <DetailRow
                icon={Building2}
                label="City"
                value={siteData?.city || "Not set"}
              />
              <DetailRow
                icon={MapPin}
                label="State"
                value={siteData?.state || "Not set"}
              />
              <DetailRow
                icon={Hash}
                label="Pincode"
                value={siteData?.pincode || "Not set"}
              />
              <DetailRow
                icon={MapPin}
                label="GPS Coordinates"
                value={
                  gpsCoordinates ? (
                    gpsCoordinates
                  ) : (
                    <span className="italic font-medium text-[#C0C6D4]">
                      Not set
                    </span>
                  )
                }
              />
              <DetailRow
                icon={Landmark}
                label="Local admin body"
                value={siteData?.local_admin_body_name || "Not set"}
              />
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-[#EEF0F6] bg-white p-4 shadow-[0_14px_34px_rgba(15,23,42,0.04)]">
          <div className="mb-4 flex items-center gap-2 text-[15px] font-semibold text-[#1F2937]">
            <Clock3 className="h-5 w-5 text-[#8B5CF6]" />
            <span>Activity</span>
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            <DetailRow
              icon={UserRound}
              label="Created by"
              value={
                siteData?.created_by || siteData?.owner_name || "Not available"
              }
            />
            <DetailRow
              icon={CalendarDays}
              label="Created on"
              value={formatDateTime(siteData?.created_on)}
            />
            <DetailRow
              icon={Pencil}
              label="Last updated by"
              value={siteData?.last_updated_by || "Not available"}
            />
            <DetailRow
              icon={RefreshCcw}
              label="Last updated"
              value={formatDateTime(siteData?.last_updated_on)}
            />
          </div>
        </section>
      </div>

      {editModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4 py-6"
          onClick={() => setEditModalOpen(false)}
        >
          <div
            className="relative w-full max-w-3xl overflow-y-auto rounded-lg border border-[#E9EDF5] bg-white p-5 shadow-[0_24px_60px_rgba(15,23,42,0.16)] max-h-[90vh] lg:p-6"
            style={{
              scrollbarWidth: "none",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setEditModalOpen(false)}
              className="absolute right-4 top-4 rounded-full p-2 text-gray-500 transition hover:bg-[#F7F8FB] hover:text-gray-700"
            >
              <X size={20} />
            </button>

            <div className="mb-5 border-b border-[#EEF1F6] pb-4 pr-10">
              <h2 className="text-lg font-semibold text-[#111827]">
                Edit Site Details
              </h2>
              <p className="mt-1 text-sm text-[#8A94A6]">
                Update project information, address, and planning details.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="rounded-md border border-[#EEF1F6] bg-[#FBFCFE] p-4">
                <div className="mb-3 text-sm font-semibold text-[#111827]">
                  Core Details
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {/* Project Name */}
                  <div>
                    <label className={detailFieldLabelClass}>
                      Project Name
                    </label>
                    <input
                      type="text"
                      value={formData.site_name}
                      onChange={(e) =>
                        setFormData({ ...formData, site_name: e.target.value })
                      }
                      className={detailFieldInputClass}
                    />
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className={detailFieldLabelClass}>
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) =>
                        setFormData({ ...formData, start_date: e.target.value })
                      }
                      className={detailFieldInputClass}
                    />
                  </div>

                  {/* Handover Date */}
                  <div>
                    <label className={detailFieldLabelClass}>
                      Handover Date
                    </label>
                    <input
                      type="date"
                      value={formData.handover_date}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          handover_date: e.target.value,
                        })
                      }
                      className={detailFieldInputClass}
                    />
                  </div>

                  <div>
                    <label className={detailFieldLabelClass}>
                      Estimate Budget
                    </label>
                    <input
                      type="number"
                      value={formData.esrimated_cost}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          esrimated_cost: e.target.value,
                        })
                      }
                      className={detailFieldInputClass}
                    />
                  </div>

                  <div>
                    <label className={detailFieldLabelClass}>
                      Construction Stage
                    </label>
                    <select
                      value={formData.construction_stage || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          construction_stage: e.target.value,
                        })
                      }
                      className={detailFieldInputClass}
                    >
                      <option value="">Select Stage</option>
                      {constructionStageOptions.map((stage) => (
                        <option key={stage} value={stage}>
                          {stage}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Project Overview */}
              <div className="rounded-md border border-[#EEF1F6] bg-[#FBFCFE] p-4">
                <label className="text-[13px] font-semibold text-[#1F2937]">
                  Project Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                  className={detailFieldInputClass}
                />
              </div>

              {/* Project Address Section */}
              <div className="rounded-md border border-[#EEF1F6] bg-[#FBFCFE] p-4">
                <label className="mb-3 block text-[13px] font-semibold text-[#1F2937]">
                  Project Address
                </label>

                {/* Address Line  */}
                <input
                  type="text"
                  value={formData.address || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                  placeholder="Street address"
                  className={`${detailFieldInputClass} mb-3 mt-0`}
                />

                {/* Address Line 2 */}
                {/* <input
                  type="text"
                  value={formData.addressLine2 || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, addressLine2: e.target.value })
                  }
                  placeholder="Address line 2"
                  className="w-full mb-3 p-2 border border-gray-300 rounded"
                /> */}

                {/* City and State dropdowns */}
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div>
                    <label className={`mb-1 block ${detailFieldLabelClass}`}>
                      State
                    </label>
                    <select
                      value={formData.state || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          state: e.target.value,
                          city: "",
                        })
                      }
                      className={`${detailFieldInputClass} mt-0`}
                    >
                      <option value="">Select State</option>
                      {states.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`mb-1 block ${detailFieldLabelClass}`}>
                      City
                    </label>
                    <select
                      value={formData.city || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, city: e.target.value })
                      }
                      className={`${detailFieldInputClass} mt-0`}
                    >
                      <option value="">Select City</option>
                      {cities.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`mb-1 block ${detailFieldLabelClass}`}>
                      Pincode
                    </label>
                    <input
                      type="text"
                      value={formData.pincode || ""}
                      onChange={(e) => {
                        const val = (e.target.value || "")
                          .replace(/\D/g, "")
                          .slice(0, 6); // only digits, max 6
                        setFormData({ ...formData, pincode: val });
                        if (val.length === 6) handlePinChange(val); // fetch only when valid 6-digit PIN
                      }}
                      placeholder="Pincode"
                      className={`${detailFieldInputClass} mt-0`}
                    />
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className={`mb-1 block ${detailFieldLabelClass}`}>
                      Latitude
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={formData.latitude || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, latitude: e.target.value })
                      }
                      placeholder="e.g. 22.5726"
                      className={`${detailFieldInputClass} mt-0`}
                    />
                  </div>
                  <div>
                    <label className={`mb-1 block ${detailFieldLabelClass}`}>
                      Longitude
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={formData.longitude || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, longitude: e.target.value })
                      }
                      placeholder="e.g. 88.3639"
                      className={`${detailFieldInputClass} mt-0`}
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-[#EEF1F6] bg-[#FBFCFE] p-4">
                <div className="mb-3 text-sm font-semibold text-[#111827]">
                  Classification
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className={detailFieldLabelClass}>
                      Building Category
                    </label>
                    <select
                      value={formData.site_catagory_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          site_catagory_id: e.target.value,
                        })
                      }
                      className={detailFieldInputClass}
                    >
                      <option value="">Select</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.catagory_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Admin Body */}
                  <div>
                    <label className={detailFieldLabelClass}>
                      Administrative Body
                    </label>
                    <select
                      value={formData.local_admin_body_id}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selectedName =
                          adminBodies.find(
                            (ab) => ab.id === parseInt(selectedId),
                          )?.admin_body_type || "";
                        setFormData({
                          ...formData,
                          local_admin_body_id: selectedId,
                          local_admin_body_name: selectedName,
                        });
                      }}
                      className={detailFieldInputClass}
                    >
                      <option value="">Select</option>
                      {adminBodies.map((body) => (
                        <option key={body.id} value={body.id}>
                          {body.admin_body_type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Project Area & Unit */}
              <div className="rounded-md border border-[#EEF1F6] bg-[#FBFCFE] p-4">
                <div className="mb-3 text-sm font-semibold text-[#111827]">
                  Area
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className={detailFieldLabelClass}>
                      Project Area
                    </label>
                    <input
                      type="number"
                      value={formData.site_area}
                      onChange={(e) =>
                        setFormData({ ...formData, site_area: e.target.value })
                      }
                      className={detailFieldInputClass}
                    />
                  </div>
                  <div>
                    <label className={detailFieldLabelClass}>Unit</label>
                    <select
                      value={formData.site_unit}
                      onChange={(e) =>
                        setFormData({ ...formData, site_unit: e.target.value })
                      }
                      className={detailFieldInputClass}
                    >
                      <option value="m_sq">Sq. Meter</option>
                      <option value="ft_sq">Sq. Ft.</option>
                      <option value="acre">Acre</option>
                      <option value="hectare">Hectare</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Update Button */}
            <div className="mt-5 flex items-center justify-end gap-3 border-t border-[#EEF1F6] pt-4">
              <button
                onClick={() => setEditModalOpen(false)}
                className="rounded-xl border border-[#D7DBE6] px-4 py-2 text-sm font-semibold text-[#4B5563] transition hover:bg-[#F9FAFB]"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const siteId = localStorage.getItem("site");
                  const token = localStorage.getItem("access");

                  // Build a safe payload: map fields, coerce numerics, and drop empty strings
                  const cleaned = {
                    ...formData,
                    // Map UI field to API field
                    uom: formData.site_unit || undefined,
                    // Numeric coercions (blank -> null)
                    esrimated_cost:
                      formData.esrimated_cost === "" ||
                      formData.esrimated_cost === null
                        ? null
                        : Number(formData.esrimated_cost),
                    site_area:
                      formData.site_area === "" || formData.site_area === null
                        ? null
                        : Number(formData.site_area),
                    pincode:
                      formData.pincode === "" || formData.pincode === null
                        ? null
                        : String(formData.pincode).length === 6
                          ? Number(formData.pincode)
                          : undefined, // if not valid length, omit
                    latitude:
                      formData.latitude === "" || formData.latitude === null
                        ? null
                        : Number(formData.latitude),
                    longitude:
                      formData.longitude === "" || formData.longitude === null
                        ? null
                        : Number(formData.longitude),
                    // Coerce FK IDs to numbers when present
                    site_catagory_id:
                      formData.site_catagory_id === "" ||
                      formData.site_catagory_id === null
                        ? undefined
                        : Number(formData.site_catagory_id),
                    local_admin_body_id:
                      formData.local_admin_body_id === "" ||
                      formData.local_admin_body_id === null
                        ? undefined
                        : Number(formData.local_admin_body_id),
                    construction_stage:
                      formData.construction_stage === "" ||
                      formData.construction_stage === null
                        ? undefined
                        : formData.construction_stage,
                    project_stage:
                      formData.construction_stage === "" ||
                      formData.construction_stage === null
                        ? undefined
                        : formData.construction_stage,
                    constructionStage:
                      formData.construction_stage === "" ||
                      formData.construction_stage === null
                        ? undefined
                        : formData.construction_stage,
                  };

                  // Remove keys with empty string or undefined values
                  const updatedPayload = Object.fromEntries(
                    Object.entries(cleaned).filter(
                      ([, v]) => v !== "" && v !== undefined,
                    ),
                  );
                  // Do not send UI-only key
                  delete updatedPayload.site_unit;
                  try {
                    await axios.patch(
                      `${BASE_URL}/sites/update-sites/${siteId}/`,
                      updatedPayload,
                      { headers: { Authorization: `Bearer ${token}` } },
                    );
                    fetchSiteDetails(); // refresh
                    setEditModalOpen(false);
                    addAlert("Site details updated successfully", "success");
                  } catch (err) {
                    console.error("Error updating details:", err);
                  }
                }}
                className="rounded-xl bg-[#3B0069] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#3B0069D6]"
              >
                Update Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-lg">
            <h3 className="text-lg font-semibold mb-4">Confirm deletion</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this site? This action cannot be
              undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="px-4 py-2 rounded bg-gray-100"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteSite}
                disabled={deleting}
                className="px-4 py-2 rounded bg-red-600 text-white"
              >
                {deleting ? "Deleting..." : "Delete Site"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default SiteDetails;